import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Application } from '@enums/application.enums';
import { PORTFOLIO_CONSTS } from '@constants/constant';
import { PAYMENT_FOR_TYPE } from '@constants/constant';
import { appRoutingUrl } from '@enums/comman.enums';
import { API_URLS } from '@core/api-url.enum';
import { AuthService } from '@core/auth.service';
import { CommonService } from '@modules/shared/services/common.service';
import { PermissionService } from '@shared/services/permissions.service';
import { ApiCallService } from '@services/api-call.service';
import { PostSubscriptionListPopupComponent } from '@shared/components/post-subscription-list-popup/post-subscription-list-popup.component';
import { UnlockNftComponent } from '@shared/components/unlock-nft/unlock-nft.component';
import { LoginDialogComponent } from '@shared/components/login-dialog/login-dialog.component';

export interface MusicUnlockConfig {
  selectedItem: any;
  visitedUserProfile: any;
  userSubscriptionList: any[];
  pUserSubscriptionList: any[];
  portfolioType: string;
  unlockModal?: any;
  destroy$?: Subject<void>;
  isIndividual?: boolean;
  userId?: string;
  portfolioTypeId?: string;
  isAlbumLocking?: boolean;
  isLivestreamContext?: boolean;
  onUnlockModalClose?: () => void;
  onMusicPlayerModalClose?: () => void;
}

@Injectable({
  providedIn: 'root'
})
export class PortfolioUnlockService {
  constructor(
    private readonly authService: AuthService,
    private readonly cs: CommonService,
    private readonly ps: PermissionService,
    private readonly apiCallService: ApiCallService,
    private readonly router: Router,
    private readonly modal: NgbModal,
    private readonly modalService: BsModalService
  ) { }

  handleLockClick(item: any, event: Event, config: MusicUnlockConfig): boolean {
    if (!this.authService.isLogin) {
      this.loginDialog();
      return false;
    }

    event?.stopPropagation();
    config.selectedItem = item;

    if (config.unlockModal) {
      config.unlockModal.unlockModalOpen(item);
    }

    return true;
  }

  purchaseSubscription(config: MusicUnlockConfig): void {
    if (
      !this.cs.isBrowser ||
      (config.selectedItem?.unlocked_through?.hasOwnProperty(
        'is_outer_unlocked'
      ) &&
        !config.selectedItem?.unlocked_through?.is_outer_unlocked)
    ) {
      return;
    }

    this.closeUnlockModal(config);

    if (this.authService.isLogin) {
      const cb = () => {
        const lockedSubscriptions =
          config.selectedItem?.lock_through?.subscription || [];

        const subscriptions = config.userSubscriptionList?.filter((userSub) =>
          lockedSubscriptions?.some((lockedSub) => lockedSub.sk === userSub.sk)
        );

        const data = {
          plan_user_id: config.visitedUserProfile?.gsi4pk,
          is_individual_price:
            config.selectedItem?.lock_through?.individual?.individual_price,
          individual_price:
            config.selectedItem?.lock_through?.individual?.individual_price,
          subscription: subscriptions,
          is_subscription:
            config.userSubscriptionList.length !== Application.ZERO,
          sk: config.selectedItem?.sk,
          downloadables_id:
            config.selectedItem?.gsi2sk || config.selectedItem?.id,
          user: {
            email:
              config.selectedItem?.pk?.split('#')?.[Application.ONE]?.trim() ||
              config.visitedUserProfile?.email
          }
        };

        const livestreamWindowClass = config.isLivestreamContext
          ? 'modal-over-music-player livestream-desktop-right-modal'
          : 'modal-over-music-player';
        const modalRef = this.modal.open(PostSubscriptionListPopupComponent, {
          modalDialogClass: config.isLivestreamContext ? 'modal-lg post-price-payment-modal livestream-desktop-right-modal' : 'modal-lg post-price-payment-modal',
          windowClass: livestreamWindowClass,
          backdrop: 'static'
        });
        modalRef.componentInstance.data = {
          ...data,
          label:
            config.portfolioType === PORTFOLIO_CONSTS.TYPE.MUSIC
              ? PORTFOLIO_CONSTS.MUSIC
              : PORTFOLIO_CONSTS.PORTFOLIO_ITEM,
          subscribedPlan: config.pUserSubscriptionList
        };
        modalRef.result
          .then((res) => {
            if (res) {
              this.goToPaymentPage(res, config);
            }
          })
          .catch(() => { });
      };
      this.ps.checkDelegateUserPermission(cb.bind(this));
    } else {
      this.loginDialog();
    }
  }

  getPurchaseMessage(config: MusicUnlockConfig) {
    const portfolioType = config?.portfolioType?.toLowerCase();
    const isMusic = portfolioType === PORTFOLIO_CONSTS.TYPE.MUSIC.toLowerCase();

    if (config.isAlbumLocking) {
      return isMusic
        ? PORTFOLIO_CONSTS.ALBUM_SM
        : PORTFOLIO_CONSTS.SERIES_SM;
    }

    if (isMusic) {
      return PORTFOLIO_CONSTS.SONG;
    }

    return portfolioType;
  }


  purchaseIndividual(config: MusicUnlockConfig): void {
    if (
      !this.cs.isBrowser ||
      (config.selectedItem?.unlocked_through?.hasOwnProperty(
        'is_outer_unlocked'
      ) &&
        !config.selectedItem?.unlocked_through?.is_outer_unlocked)
    ) {
      return;
    }

    this.closeUnlockModal(config);

    if (this.authService.isAuthenticated()) {
      const reqData = {
        creator_id: config.visitedUserProfile?.gsi4pk,
        content_sk: config.selectedItem?.sk,
        content_type: config.isAlbumLocking ? `ALBUM_${config.portfolioType}` : config.portfolioType
      };

      const price = config.selectedItem?.price ? config.selectedItem?.price : config.selectedItem?.lock_through?.individual?.individual_price

      const paymentData = {
        user: config.visitedUserProfile ? config.visitedUserProfile : null,
        amount: config.selectedItem?.base_price ? config.selectedItem?.base_price : config.selectedItem?.lock_through?.individual?.individual_price,
        currency_symbol: config.selectedItem?.currency_symbol,
        localPrice: Number(price?.toString()),
        exchangeRateUSD: config.selectedItem?.exchange_rate_to_usd
      };

      const destroy$ = config.destroy$ || new Subject<void>();
      this.apiCallService
        .post(API_URLS.portfolioContentPurchase, reqData, false)
        .pipe(takeUntil(destroy$))
        .subscribe({
          next: (res) => {
            if (res.status) {
              delete config?.selectedItem?.orderSK;
              delete config?.selectedItem?.callType;
              const portfolioType = this.getPurchaseMessage(config);
              const paymentPayload = {
                unlockingMsg: `${portfolioType} named ${config?.selectedItem?.title}`,
                orderSK: res?.data?.order_sk,
                ...config.selectedItem,
                ...paymentData,
                callType: PAYMENT_FOR_TYPE.MUSIC_PORTFOLIO,
                ...(config.isLivestreamContext && {
                  comingFrom: PAYMENT_FOR_TYPE.LIVE_STREAM,
                  skipPaymentApi: true
                })
              };

              if (config.isLivestreamContext) {
                this.closeAllModals(config);
                this.cs.liveStreamPaymentInitiate.next(paymentPayload);
              } else {
                this.closeAllModals(config);
                this.cs.subscriptionPaymentData.next(paymentPayload);

                if (
                  config.isIndividual &&
                  config.userId &&
                  config.portfolioTypeId
                ) {
                  this.router.navigate([appRoutingUrl.DASHBOARD_PAYMENT], {
                    queryParams: {
                      redirect: PAYMENT_FOR_TYPE.MUSIC_PORTFOLIO,
                      url: `/dashboard/portfolio/${config.userId}/banner-view/${config.portfolioTypeId}`
                    }
                  });
                } else {
                  this.router.navigate([appRoutingUrl.DASHBOARD_PAYMENT], {
                    queryParams: {
                      redirect: PAYMENT_FOR_TYPE.STORE_CONTENT,
                      url: PAYMENT_FOR_TYPE.STORE_CONTENT
                    }
                  });
                }
              }
            }
          },
          error: (err) => {
            this.cs.showErrorMessage(err.message);
          }
        });
    } else {
      this.loginDialog();
    }
  }

  purchaseNft(config: MusicUnlockConfig): void {
    if (
      !this.cs.isBrowser ||
      (config.selectedItem?.unlocked_through?.hasOwnProperty(
        'is_outer_unlocked'
      ) &&
        !config.selectedItem?.unlocked_through?.is_outer_unlocked)
    ) {
      return;
    }

    this.closeUnlockModal(config);

    if (this.authService.isLogin) {
      if (config.isLivestreamContext) {
        this.closeAllModals(config);
      }
      const options: { backdrop: 'static'; class?: string } = {
        backdrop: 'static',
        ...(config.isLivestreamContext && { class: 'livestream-desktop-right-modal' })
      };
      this.modalService.show(UnlockNftComponent, options);
    } else {
      this.loginDialog();
    }
  }

  private goToPaymentPage(paymentData: any, config: MusicUnlockConfig): void {
    if (!this.cs.isBrowser) return;

    const destroy$ = config.destroy$ || new Subject<void>();
    this.apiCallService
      .post(API_URLS.subscribeUserPlan, paymentData, false)
      .pipe(takeUntil(destroy$))
      .subscribe({
        next: (res) => {
          if (res.status) {
            delete config?.selectedItem?.orderSK;
            delete config?.selectedItem?.callType;
            delete paymentData?.orderSK;
            delete paymentData?.callType;
            const portfolioType =
              config?.portfolioType.toLowerCase() ===
                PORTFOLIO_CONSTS.TYPE.MUSIC.toLowerCase()
                ? PORTFOLIO_CONSTS.SONG
                : config?.portfolioType.toLowerCase();
            const paymentPayload = {
              unlockingMsg: `${portfolioType} named ${config?.selectedItem?.title}`,
              orderSK: res?.data?.orderSK,
              callType: PAYMENT_FOR_TYPE.MUSIC_PORTFOLIO,
              ...config.selectedItem,
              ...paymentData,
              ...(config.isLivestreamContext && {
                comingFrom: PAYMENT_FOR_TYPE.LIVE_STREAM,
                skipPaymentApi: true
              })
            };

            if (config.isLivestreamContext) {
              this.closeAllModals(config);
              this.cs.liveStreamPaymentInitiate.next(paymentPayload);
            } else {
              this.closeAllModals(config);
              this.cs.subscriptionPaymentData.next(paymentPayload);

              if (
                config.isIndividual &&
                config.userId &&
                config.portfolioTypeId
              ) {
                this.router.navigate([appRoutingUrl.DASHBOARD_PAYMENT], {
                  queryParams: {
                    redirect: PAYMENT_FOR_TYPE.MUSIC_PORTFOLIO,
                    url: `/dashboard/portfolio/${config.userId}/banner-view/${config.portfolioTypeId}`
                  }
                });
              } else {
                this.router.navigate([appRoutingUrl.DASHBOARD_PAYMENT], {
                  queryParams: {
                    redirect: PAYMENT_FOR_TYPE.STORE_CONTENT,
                    url: PAYMENT_FOR_TYPE.STORE_CONTENT
                  }
                });
              }
            }
          }
        },
        error: (err) => {
          this.cs.showErrorMessage(err.message);
        }
      });
  }

  loginDialog(): void {
    this.modalService.show(LoginDialogComponent, {});
  }

  private closeUnlockModal(config: MusicUnlockConfig): void {
    if (config.unlockModal) {
      config.unlockModal.unlockModalClose();
    } else if (config.onUnlockModalClose) {
      config.onUnlockModalClose();
    }
  }

  private closeAllModals(config: MusicUnlockConfig): void {
    if (!this.cs.isBrowser) return;

    if (config.onMusicPlayerModalClose) {
      config.onMusicPlayerModalClose();
    }

    try {
      this.modalService.setDismissReason('purchase_completed');
      this.modalService.hide();
      setTimeout(() => {
        this.modalService.hide();
      }, Application.FIFTY);
    } catch (error) {
      this.modalService.hide();
    }
  }
}
